import { useContext, useEffect, useRef } from 'react'
import { classes, media, style } from 'typestyle'
import { useLocation } from 'react-router-dom'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { runOperator } from '../../../core/operators'
import { withRouter } from '../../../contexts/with-router'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'
import AppLayout from '../../routes/app/layout'
import { CacheContext } from '../../../contexts/cache-context'

/* Hooks ======================================================================================== */
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'
import useValue from '../../../hooks/use-value'
import useBoolean from '../../../hooks/use-boolean'

/* Utils ======================================================================================== */
import { get } from '../../../share/find'
import { flat } from '../../../share/obj-equal'

/* Components =================================================================================== */
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Line } from '../../../components/line'
import { Snippet } from '../../../modules/layouts/snippet'
import { Tabs, Tab } from '../../../components/tab'
import { Text } from '../../../components/text/text.component'
import { Tooltip } from '../../../components/tooltip'
import { renderView } from '../../../modules/handlers/render-view'

/* Styles ======================================================================================= */
import { paddingHorizontalInner, paddingLeftInner } from '../../../styles/default/padding'
import { mb32 } from '../../../styles/styleset/margin/mb32'
import { mb8 } from '../../../styles/styleset/margin/mb8'
import { mt8 } from '../../../styles/styleset/margin/mt8'
import { f16i } from '../../../styles/styleset/font-size/f16i'
import { p12 } from '../../../styles/styleset/padding/p12'
import { b0 } from '../../../styles/styleset/bottom/b0'
import { p8 } from '../../../styles/styleset/padding/p8'
import { pl16i } from '../../../styles/styleset/padding/pl16i'
import { pr16i } from '../../../styles/styleset/padding/pr16i'
import { f20i } from '../../../styles/styleset/font-size/f20i'
import { mr4 } from '../../../styles/styleset/margin/mr4'

export const HiddenFields = (props: any) => {
  const { item, hiddenFields, $hiddenFields, $isTriggered, onShow } = props
  const [isShowingHiddenFields, $isShowingHiddenFields] = useBoolean()

  return (
    <Box
      className={classes(
        mb32,
        style({
          pageBreakInside: 'avoid',
        }),
      )}
      fullWidth
    >
      <Box
        spaceBetween
        alignCenterY
        onClick={$isShowingHiddenFields.toggle}
        className={style({ cursor: 'pointer' })}
      >
        <Text baseline="surface" size="xs" semiBold>
          Hidden Fields ({Object.keys(hiddenFields).length})
        </Text>
        <i className={classes('material-icons', f16i)}>
          {isShowingHiddenFields ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
        </i>
      </Box>
      <Box
        className={classes(
          mt8,
          mb8,
          style({
            pageBreakInside: 'avoid',
          }),
        )}
        row
      >
        <Line dark />
      </Box>
      {isShowingHiddenFields && (
        <>
          {Object.keys(hiddenFields).map(name => {
            return (
              <Box key={name} spaceBetween alignCenterY className={mb8}>
                <Box row>
                  <Text size="sm" shrink>
                    {hiddenFields[name].label}
                  </Text>
                </Box>
                <Button
                  label={'Show'}
                  size="xs"
                  baseline="background"
                  onClick={() => {
                    onShow(name)
                  }}
                />
              </Box>
            )
          })}
        </>
      )}
    </Box>
  )
}

const TabSearch = ({
  onSubmit,
  onReset,
  value,
}: {
  onSubmit: any
  onReset: any
  value?: string
}) => {
  const [filterText, $filterText] = useValue('')

  useEffect(() => {
    $filterText.set(value)
  }, [value])

  return (
    <Box className={classes(style({ bottom: -4, zIndex: 9 }), b0)} sticky row shadow rounded>
      {/* <Line /> */}
      <form
        onSubmit={(e: any) => {
          e.preventDefault()
          onSubmit(filterText)
        }}
      >
        <Box className={classes(p12, pl16i, pr16i)} baseline="none">
          <Box className={p8} baseline="background" rounded flex spaceBetween alignCenterY>
            <Tooltip label="Search fields">
              <i className={classes('material-icons', f20i, mr4)}>search</i>
            </Tooltip>
            <input
              value={filterText}
              onChange={(e: any) => {
                $filterText.set(e.target.value)
              }}
              style={{
                border: 'none',
                width: '100%',
                outline: 'none',
                background: 'none',
              }}
              placeholder="Type to search field(s)"
            />
            {value && (
              <Button
                size="xs"
                label="Reset"
                square
                baseline="surface"
                className={mr4}
                onClick={onReset}
              />
            )}
            <Button size="xs" label="Search" square baseline="primary" type="submit" />
          </Box>
        </Box>
      </form>
    </Box>
  )
}
let timeoutFilterTabs: any
export default addSnippet(
  'RowView',
  withRouter((props: any) => {
    const timeout = useRef<{ highlight: any; hide: any }>({ highlight: null, hide: null })
    const { update, trigger, sync, allowHiddenFields, allowHighlightFields } = props
    const [local] = useLocal()
    const cacheContext = useContext(CacheContext)
    const { navigation } = useContext<any>(NavigationContext)
    const location = useLocation()

    const apiUrl = `${process.env.REACT_APP_API_URL}${location.pathname}`
    const base = get('payload.base', navigation, 'object')
    const sections = get('payload.base.form.sections', navigation, 'array')
    const fields = get('payload.base.form.fields', navigation, 'array')
    const lookups = get('row.lookups', base, 'array')
    const search = { lookups }

    const [row, $row] = useFetch<any>({}, { url: apiUrl, search })
    const [rowMeta, $rowMeta] = useFetch({}, { url: apiUrl, search })
    const [hiddenFields, $hiddenFields] = useValue({})
    const [highlightedFields, $highlightedFields] = useValue({})
    const [isTriggered, $isTriggered] = useBoolean(false)
    const [isShowingHiddenFields, $isShowingHiddenFields] = useBoolean()

    const [filterText, $filterText] = useValue()
    const [filteredTabsState, $filteredTabsState] = useValue({})
    const filteredTabs = useRef({})

    const appLayoutContext = useContext(AppLayout.context)
    const { getOffsetTop = () => 0 } = appLayoutContext || ({} as any)
    const [isExpanded, $isExpanded] = useBoolean(props.isExpandable ? false : true)

    useEffect(() => {
      if (
        Object.keys(filteredTabs.current) &&
        JSON.stringify(filteredTabs.current) !== JSON.stringify(filteredTabsState)
      ) {
        clearTimeout(timeoutFilterTabs)
        timeoutFilterTabs = setTimeout(() => {
          $filteredTabsState.set({ ...filteredTabs.current })
        }, 1000)
      }
    }, [filteredTabs.current])

    useEffect(() => {
      if (row.mounted && Object.keys(base).length > 0) {
        $row.get({ url: apiUrl, search }).then((payload: any) => {
          props.$row?.set(payload)
          cacheContext?.$value?.set?.(prev => ({
            ...prev,
            parentRow: payload?.parentRow?.id,
            row: payload,
          }))

          const rawHiddenFields = payload.body?.meta?.hiddenFields || {}

          for (const key in rawHiddenFields) {
            rawHiddenFields[key] = rawHiddenFields[key]
          }

          $hiddenFields.set(rawHiddenFields)

          const rawHighlightedFields = payload.body?.meta?.highlightedFields || {}

          for (const key in rawHighlightedFields) {
            rawHighlightedFields[key] = flat(rawHighlightedFields[key])
          }

          $highlightedFields.set(rawHighlightedFields)
        })
      }
    }, [row.mounted, base])

    useEffect(() => {
      if (trigger) {
        $row.get({ url: apiUrl, search }).then((payload: any) => {
          props.$row?.set(payload)
          cacheContext?.$value?.set?.(prev => ({
            ...prev,
            parentRow: payload?.parentRow?.id,
            row: payload,
          }))
        })
      }
    }, [trigger])

    useEffect(() => {
      const run = () => {
        if (isTriggered && rowMeta.mounted && Object.keys(base).length > 0) {
          $rowMeta.kill()
          $rowMeta.patch({
            url: apiUrl,
            body: {
              replace: 'meta.hiddenFields',
              body: {
                meta: {
                  hiddenFields,
                },
              },
            },
          })
        }
      }

      clearTimeout(timeout.current.hide)
      timeout.current.hide = setTimeout(run, 100)
    }, [hiddenFields])

    useEffect(() => {
      const run = () => {
        if (isTriggered && rowMeta.mounted && Object.keys(base).length > 0) {
          $rowMeta.kill()
          $rowMeta.patch({
            url: apiUrl,
            body: {
              replace: 'meta.highlightedFields',
              body: {
                meta: {
                  highlightedFields,
                },
              },
            },
          })
        }
      }

      clearTimeout(timeout.current.highlight)
      timeout.current.highlight = setTimeout(run, 100)
    }, [highlightedFields])

    const parseHiddenFields = (a: any = {}) => {
      const parsed: any = {}
      for (const key in a) {
        parsed[key.replaceAll('_', '.')] = a[key]
      }

      return parsed
    }

    const renderHiddenFields = (item?: any) => {
      const initialContainer = (item ? hiddenFields[`body_${item.name}`] : hiddenFields) || {}
      const container: any = {}

      for (const key in initialContainer) {
        if (!initialContainer[key].parentLabel) {
          container[key] = initialContainer[key]
        }
      }

      return (
        Object.keys(container).length > 0 && (
          <Box
            className={classes(
              mb32,
              style({
                pageBreakInside: 'avoid',
              }),
            )}
            fullWidth
          >
            <Box
              spaceBetween
              alignCenterY
              onClick={$isShowingHiddenFields.toggle}
              className={style({ cursor: 'pointer' })}
            >
              <Text baseline="surface" size="xs" semiBold>
                Hidden Fields ({Object.keys(container).length})
              </Text>
              <i className={classes('material-icons', f16i)}>
                {isShowingHiddenFields ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
              </i>
            </Box>
            <Box
              className={classes(
                mt8,
                mb8,
                style({
                  pageBreakInside: 'avoid',
                }),
              )}
              row
            >
              <Line dark />
            </Box>
            {isShowingHiddenFields && (
              <>
                {Object.keys(container).map(name => {
                  return (
                    <Box key={name} spaceBetween alignCenterY className={mb8}>
                      <Box row>
                        <Text size="sm" shrink>
                          {container[name].label}
                        </Text>
                      </Box>
                      <Button
                        label={'Show'}
                        size="xs"
                        baseline="background"
                        onClick={() => {
                          $isTriggered.set(true)
                          $hiddenFields.set((prev: any) => {
                            const newPrev = { ...prev }
                            if (item) {
                              delete newPrev[`body_${item.name}`][name]
                            } else {
                              delete newPrev[name]
                            }
                            return newPrev
                          })
                        }}
                      />
                    </Box>
                  )
                })}
              </>
            )}
          </Box>
        )
      )
    }

    if (Object.keys(row.payload).length === 0) {
      return <></>
    }

    return (
      <Snippet>
        <Box baseline="none" relative>
          {(() => {
            const flatten: any = flat(row.payload)

            const value: any = row.payload
            fields &&
              fields.map((field: any) => {
                switch (field.type) {
                  default:
                    if (field?.name?.indexOf('body.') === -1) field.name = `body.${field.name}`
                    break
                }
              })
            if (sections && sections.length > 0) {
              return (
                <>
                  <Tabs
                    topOffset={getOffsetTop()}
                    footer={
                      props.isSearchable && (
                        <TabSearch
                          value={filterText}
                          onReset={() => {
                            filteredTabs.current = {}
                            for (let i = 0; i < sections.length; i++) {
                              filteredTabs.current[i] = true
                            }
                            $filteredTabsState.set(filteredTabs.current)
                            $filterText.set('')
                          }}
                          onSubmit={filterText => {
                            filteredTabs.current = {}
                            for (let i = 0; i < sections.length; i++) {
                              filteredTabs.current[i] = true
                            }
                            $filteredTabsState.set(filteredTabs.current)
                            $filterText.set(filterText)
                          }}
                        />
                      )
                    }
                  >
                    {sections &&
                      sections.map((item: any, key: number) => {
                        const { hidden } = runOperator(item, {
                          row: flat(row.payload),
                        })

                        return (
                          <Tab
                            label={`${item.label}`}
                            key={key}
                            hidden={hidden}
                            highlight={filterText && filteredTabsState[key] === false}
                          >
                            <Box
                              maxHeight={
                                props.isExpandable ? (isExpanded ? 10000 : 108) : undefined
                              }
                              relative
                              className={classes(
                                paddingLeftInner,
                                paddingHorizontalInner,
                                style({
                                  overflow: 'hidden',
                                  transitionProperty: 'max-height',
                                  ...media({ type: 'print' }, { marginTop: 50 }),
                                  $nest: {
                                    '.aeki-box': {
                                      pageBreakInside: 'avoid',
                                    },
                                  },
                                }),
                              )}
                              baseline="none"
                            >
                              {item.fields &&
                                renderView({
                                  fields: item.fields,
                                  hiddenFields: parseHiddenFields(
                                    hiddenFields[`body_${item.name}`],
                                  ),
                                  highlightedFields: highlightedFields[`body_${item.name}`] || {},
                                  parentName: `body.${item.name}`,
                                  parentRowId: row?.payload?.parentRow?.id,
                                  flatten,
                                  value,
                                  form: value,
                                  labelFilter: filterText,
                                  onLabelFilter: toggled => {
                                    if (filteredTabs.current[key] === undefined) {
                                      filteredTabs.current = {
                                        ...filteredTabs.current,
                                        [key]: true,
                                      }
                                    } else if (toggled === false) {
                                      filteredTabs.current = {
                                        ...filteredTabs.current,
                                        [key]: false,
                                      }
                                    }
                                  },
                                  isEditable: props.disableEdit ? false : base.row.isEditable,
                                  pathName: location.pathname,
                                  local,
                                  onUpdate: () => {
                                    update?.()
                                    $row.get({ url: apiUrl, search }).then((payload: any) => {
                                      props.$row?.set(payload)
                                      sync()
                                    })
                                  },
                                  onShow: (name: any) => {
                                    $isTriggered.set(true)
                                    $hiddenFields.set((prev: any) => {
                                      const newPrev = { ...prev }
                                      if (item) {
                                        delete newPrev[`body_${item.name}`][
                                          name.replaceAll('.', '_')
                                        ]
                                      } else {
                                        delete newPrev[name.replaceAll('.', '_')]
                                      }
                                      return newPrev
                                    })
                                  },
                                  onHide: allowHiddenFields
                                    ? (field: any) => {
                                        $isTriggered.set(true)
                                        $hiddenFields.set((prev: any) => ({
                                          ...prev,
                                          [`body_${item.name}`]: {
                                            ...prev[`body_${item.name}`],
                                            [field.name.replaceAll('.', '_')]: field,
                                          },
                                        }))
                                        $isShowingHiddenFields.set(true)
                                      }
                                    : undefined,
                                  onHighlight: allowHighlightFields
                                    ? (name: any) => {
                                        $isTriggered.set(true)
                                        $highlightedFields.set((prev: any) => {
                                          const newPrev = { ...prev }
                                          if (newPrev?.[`body_${item.name}`]?.[name]) {
                                            delete newPrev[`body_${item.name}`][name]

                                            return newPrev
                                          } else {
                                            return {
                                              ...newPrev,
                                              [`body_${item.name}`]: {
                                                ...newPrev[`body_${item.name}`],
                                                [name]: true,
                                              },
                                            }
                                          }
                                        })
                                      }
                                    : undefined,
                                })}
                              {allowHiddenFields && renderHiddenFields(item)}
                              {props.isExpandable && (
                                <Box
                                  absolute={!isExpanded}
                                  style={{
                                    background:
                                      'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                                    zIndex: 10,
                                    top: 0,
                                    left: 0,
                                    borderRadius: '0 0 4px 4px',
                                    padding: isExpanded ? 0 : 12,
                                  }}
                                  fullHeight
                                  fullWidth
                                  flex
                                  alignCenterX
                                  alignBottom
                                >
                                  <Button
                                    label={isExpanded ? 'Collapse' : 'Expand'}
                                    baseline="surface"
                                    float
                                    size="md"
                                    icon={
                                      <i className={classes('material-icons', f16i)}>
                                        {isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                      </i>
                                    }
                                    onClick={$isExpanded.toggle}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Tab>
                        )
                      })}
                  </Tabs>
                </>
              )
            } else if (fields && fields.length > 0) {
              return (
                <Box
                  className={classes(paddingLeftInner, paddingHorizontalInner)}
                  baseline="none"
                  relative
                >
                  {renderView({
                    fields,
                    hiddenFields: parseHiddenFields(hiddenFields),
                    highlightedFields,
                    parentName: null,
                    parentRowId: row?.payload?.parentRow?.id,
                    flatten,
                    value,
                    form: value,
                    local,
                    isEditable: props.disableEdit ? false : base.row.isEditable,
                    pathName: location.pathname,
                    onUpdate: () => {
                      update?.()
                      $row.get({ url: apiUrl, search }).then((payload: any) => {
                        props.$row?.set(payload)
                        sync()
                      })
                    },
                    onHide: allowHiddenFields
                      ? (field: any) => {
                          $isTriggered.set(true)
                          $hiddenFields.set((prev: any) => ({
                            ...prev,
                            [field.name.replaceAll('.', '_')]: field,
                          }))
                          $isShowingHiddenFields.set(true)
                        }
                      : undefined,
                    onHighlight: allowHighlightFields
                      ? (name: any) => {
                          $isTriggered.set(true)
                          $highlightedFields.set((prev: any) => {
                            const newPrev = { ...prev }
                            if (newPrev[name]) {
                              delete newPrev[name]
                              return newPrev
                            } else {
                              return { ...newPrev, [name]: true }
                            }
                          })
                        }
                      : undefined,
                  })}
                  {allowHiddenFields && renderHiddenFields()}
                </Box>
              )
            }
          })()}
        </Box>
      </Snippet>
    )
  }),
)
